import { useEffect, useState } from 'react';
import { connectAPI } from '@services/photographerApi';
import type { ConnectStatus } from '@/types/photographer';

type GetPaidCardProps = {
  connectEnabled?: boolean;
};

function isStripeConnectEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_STRIPE_CONNECT === 'true';
}

/** Pull a user-safe backend error message off an Axios error, falling back to a default. */
function errorDetail(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object') return fallback;

  const detail = (data as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') {
    const detailMessage = (detail as { message?: unknown }).message;
    if (typeof detailMessage === 'string' && detailMessage.trim()) return detailMessage;
  }

  const message = (data as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

export default function GetPaidCard({ connectEnabled = isStripeConnectEnabled() }: GetPaidCardProps) {
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!connectEnabled) return;
    connectAPI.getStatus().then(setStatus).catch(() => setError('Could not load payout status.'));
  }, [connectEnabled]);

  if (!connectEnabled) return null;

  const onboard = async () => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await connectAPI.startOnboarding();
      if (url) window.location.href = url;
      else setError('Could not start payout setup. Please try again.');
    } catch (err) {
      setError(errorDetail(err, 'Could not start payout setup. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const viewPayouts = async () => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await connectAPI.getDashboardLink();
      if (url) window.location.href = url;
    } catch (err) {
      setError(errorDetail(err, 'Could not open the payouts dashboard. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const verified = status?.charges_enabled && status?.payouts_enabled;
  const inProgress = !!status?.account_id && !verified;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="text-lg font-semibold text-gray-900">Get paid</h3>
      <p className="mt-1 text-sm text-gray-500">
        Connect Stripe to receive payouts when customers buy your photos.
      </p>

      {verified && (
        <p className="mt-3 inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
          Verified
        </p>
      )}
      {inProgress && (
        <p className="mt-3 inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800">
          In progress
        </p>
      )}

      <div className="mt-4 flex gap-3">
        {verified ? (
          <button
            type="button"
            onClick={viewPayouts}
            disabled={busy}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            View payouts
          </button>
        ) : (
          <button
            type="button"
            onClick={onboard}
            disabled={busy}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {inProgress ? 'Finish payout setup' : 'Set up payouts'}
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
